import * as React from "react"
import { cn } from "cn"

/**
 * The four brand dots — teal, green, amber, red — in the order they appear on
 * delegatecomputer.com. This is Delegate's signature mark after the logo
 * itself: use it as a divider, a loading indicator, or a quiet footer stamp.
 *
 * It is decorative. It is always `aria-hidden`; if the dots are standing in
 * for a loading state, pair them with a visible or screen-reader-only label.
 */

const DOTS = ["teal", "green", "amber", "red"] as const

const sizes = {
  sm: { dot: "size-1", gap: "gap-1" },
  md: { dot: "size-1.5", gap: "gap-[7px]" },
  lg: { dot: "size-2", gap: "gap-2" },
} as const

const swatch: Record<(typeof DOTS)[number], string> = {
  teal: "bg-brand-teal",
  green: "bg-brand-green",
  amber: "bg-brand-amber",
  red: "bg-brand-red",
}

function BrandDots({
  size = "md",
  animated = false,
  className,
  ...props
}: React.ComponentProps<"span"> & {
  size?: keyof typeof sizes
  /** Cycle the dots. Suppressed automatically under prefers-reduced-motion. */
  animated?: boolean
}) {
  return (
    <span
      data-slot="brand-dots"
      aria-hidden="true"
      className={cn("inline-flex items-center", sizes[size].gap, className)}
      {...props}
    >
      {DOTS.map((name, i) => (
        <span
          key={name}
          className={cn(
            "rounded-full",
            sizes[size].dot,
            swatch[name],
            animated && "animate-[pulse-dot_2.4s_var(--ease-rise)_infinite]"
          )}
          style={animated ? { animationDelay: `${i * 160}ms` } : undefined}
        />
      ))}
    </span>
  )
}

export { BrandDots }
